import React from 'react';
import { X } from 'lucide-react';
import cn from 'classnames';

interface RealtimeModalProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl';
  className?: string;
  fullScreen?: boolean; // NEW PROP
}

const sizeClasses = {
  sm: 'lg:max-w-sm lg:h-auto h-full',
  md: 'lg:max-w-md lg:h-auto h-full',
  lg: 'lg:max-w-lg lg:h-auto h-full',
  xl: 'lg:max-w-xl lg:h-auto h-full',
  '2xl': 'lg:max-w-2xl lg:h-auto h-full',
  '3xl': 'lg:max-w-3xl lg:h-auto h-full',
  '4xl': 'lg:max-w-4xl lg:h-auto h-full',
  '5xl': 'lg:w-full h-full',
};

export const RealtimeModal = ({
  isOpen,
  onClose,
  children,
  size = 'md',
  className,
  fullScreen = false,
}: RealtimeModalProps) => {
  if (!isOpen) {
    return null;
  }

  return (
    <div
      className={cn(
        'fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 transition-opacity',
        !fullScreen && 'lg:p-5' // Only apply padding if NOT full screen
      )}
      onClick={onClose}
    >
      <div
        className={cn(
          'relative bg-card-color-100 dark:bg-gray-800 flex flex-col w-full h-full transition-all duration-300',
          !fullScreen &&
            'lg:rounded-xl lg:h-auto border border-slate-200 dark:border-gray-700 shadow-2xl',
          !fullScreen && sizeClasses[size],
          className
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
};

// ... RealtimeModal.Header, RealtimeModal.Body, RealtimeModal.Footer remain the same logic
const RealtimeModalHeader = ({
  title,
  onClose,
  className,
}: {
  title: string;
  onClose: () => void;
  className?: string;
}) => (
  <div
    className={cn(
      'flex items-center justify-between p-4 border-b border-slate-200 dark:border-gray-700',
      className
    )}
  >
    <h2 className="text-xl font-semibold text-title-page-color-100 dark:text-white">
      {title}
    </h2>
    <button
      onClick={onClose}
      className="p-1 rounded-full text-slate-400 hover:bg-slate-100 transition-colors"
    >
      <X size={20} />
    </button>
  </div>
);

const RealtimeModalBody = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div className={cn('p-4 overflow-y-auto flex-grow', className)}>
    {children}
  </div>
);

const RealtimeModalFooter = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => (
  <div
    className={cn(
      'flex items-center justify-end px-6 py-4 border-t border-slate-200 dark:border-gray-700 gap-3',
      className
    )}
  >
    {children}
  </div>
);

RealtimeModal.Header = RealtimeModalHeader;
RealtimeModal.Body = RealtimeModalBody;
RealtimeModal.Footer = RealtimeModalFooter;